/**
 * Orbitra Solution unified analytics service.
 *
 * Runtime IDs come from Vite environment variables. Scripts load only when the
 * matching ID exists; production tracking is enabled by default, with an
 * explicit VITE_ANALYTICS_DEV=true override for local QA except for Clarity.
 */

const IS_PROD = import.meta.env.PROD;
const DEV_OVERRIDE = import.meta.env.VITE_ANALYTICS_DEV === 'true';
const SHOULD_TRACK = IS_PROD || DEV_OVERRIDE;
const SHOULD_LOAD_CLARITY = IS_PROD;

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
const GTM_ID = import.meta.env.VITE_GTM_ID as string | undefined;
const CLARITY_ID = import.meta.env.VITE_CLARITY_ID as string | undefined;
const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID as string | undefined;
const LINKEDIN_PARTNER_ID = import.meta.env.VITE_LINKEDIN_PARTNER_ID as string | undefined;
const GOOGLE_ADS_ID = import.meta.env.VITE_GOOGLE_ADS_ID as string | undefined;
const TIKTOK_PIXEL_ID = import.meta.env.VITE_TIKTOK_PIXEL_ID as string | undefined;

type EventParams = Record<string, string | number | boolean | undefined>;
type PixelProvider = 'ga4' | 'gtm' | 'meta' | 'linkedin' | 'google_ads' | 'tiktok' | 'clarity';
type PixelAdapter = {
  name: PixelProvider;
  enabled: () => boolean;
  init: () => void;
  track: (eventName: string, params: EventParams) => void;
};

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    clarity?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
    ttq?: { track?: (...args: unknown[]) => void; page?: () => void; load?: (id: string) => void };
    _linkedin_data_partner_ids?: string[];
  }
}

let initialized = false;

function cleanParams(params: EventParams): Record<string, string | number | boolean> {
  return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined)) as Record<string, string | number | boolean>;
}

function injectScript(src: string, id: string, async = true, defer = false): void {
  if (document.getElementById(id)) return;
  const script = document.createElement('script');
  script.id = id;
  script.src = src;
  script.async = async;
  script.defer = defer;
  document.head.appendChild(script);
}

function injectInlineScript(content: string, id: string): void {
  if (document.getElementById(id)) return;
  const script = document.createElement('script');
  script.id = id;
  script.textContent = content;
  document.head.appendChild(script);
}

function pushDataLayer(eventName: string, params: EventParams): void {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: eventName, ...cleanParams(params) });
}

const adapters: PixelAdapter[] = [
  {
    name: 'gtm',
    enabled: () => Boolean(GTM_ID && SHOULD_TRACK),
    init: () => {
      injectInlineScript(
        `window.dataLayer=window.dataLayer||[];(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${GTM_ID}');`,
        'gtm-init',
      );
    },
    track: (eventName, params) => pushDataLayer(eventName, params),
  },
  {
    name: 'ga4',
    enabled: () => Boolean(GA_ID && SHOULD_TRACK),
    init: () => {
      injectScript(`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`, 'ga4-script');
      window.dataLayer = window.dataLayer || [];
      window.gtag = window.gtag || function gtagFallback(...args: unknown[]) {
        window.dataLayer?.push(args);
      };
      window.gtag('js', new Date());
      window.gtag('config', GA_ID, {
        page_path: window.location.pathname + window.location.search,
        page_title: document.title,
        send_page_view: false,
        anonymize_ip: true,
        cookie_flags: 'SameSite=None;Secure',
      });
    },
    track: (eventName, params) => window.gtag?.('event', eventName, cleanParams(params)),
  },
  {
    name: 'clarity',
    enabled: () => Boolean(CLARITY_ID && SHOULD_LOAD_CLARITY),
    init: () => {
      injectInlineScript(
        `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src='https://www.clarity.ms/tag/'+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,'clarity','script','${CLARITY_ID}');`,
        'clarity-init',
      );
    },
    track: (eventName, params) => window.clarity?.('event', eventName, cleanParams(params)),
  },
  {
    name: 'meta',
    enabled: () => Boolean(META_PIXEL_ID && SHOULD_TRACK),
    init: () => {
      injectInlineScript(
        `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${META_PIXEL_ID}');fbq('track','PageView');`,
        'meta-pixel-init',
      );
    },
    track: (eventName, params) => window.fbq?.('trackCustom', eventName, cleanParams(params)),
  },
  {
    name: 'linkedin',
    enabled: () => Boolean(LINKEDIN_PARTNER_ID && SHOULD_TRACK),
    init: () => {
      window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
      window._linkedin_data_partner_ids.push(LINKEDIN_PARTNER_ID as string);
      injectInlineScript(`_linkedin_partner_id='${LINKEDIN_PARTNER_ID}';window._linkedin_data_partner_ids=window._linkedin_data_partner_ids||[];window._linkedin_data_partner_ids.push(_linkedin_partner_id);`, 'linkedin-insight-init');
      injectScript('https://snap.licdn.com/li.lms-analytics/insight.min.js', 'linkedin-insight-script');
    },
    track: (eventName, params) => pushDataLayer(`linkedin_${eventName}`, params),
  },
  {
    name: 'google_ads',
    enabled: () => Boolean(GOOGLE_ADS_ID && SHOULD_TRACK),
    init: () => {
      injectScript(`https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`, 'google-ads-script');
      window.dataLayer = window.dataLayer || [];
      window.gtag = window.gtag || function gtagFallback(...args: unknown[]) {
        window.dataLayer?.push(args);
      };
      window.gtag('config', GOOGLE_ADS_ID);
    },
    track: (eventName, params) => window.gtag?.('event', eventName, { send_to: GOOGLE_ADS_ID, ...cleanParams(params) }),
  },
  {
    name: 'tiktok',
    enabled: () => Boolean(TIKTOK_PIXEL_ID && SHOULD_TRACK),
    init: () => {
      injectInlineScript(
        `!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=['page','track','identify','instances','debug','on','off','once','ready','alias','group','enableCookie','disableCookie'];ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.load=function(e){var i='https://analytics.tiktok.com/i18n/pixel/events.js',n=d.createElement('script');n.type='text/javascript';n.async=!0;n.src=i+'?sdkid='+e+'&lib='+t;var a=d.getElementsByTagName('script')[0];a.parentNode.insertBefore(n,a)};ttq.load('${TIKTOK_PIXEL_ID}');ttq.page();}(window,document,'ttq');`,
        'tiktok-pixel-init',
      );
    },
    track: (eventName, params) => window.ttq?.track?.(eventName, cleanParams(params)),
  },
];

export function initAnalytics(): void {
  if (typeof window === 'undefined' || initialized) return;
  initialized = true;
  adapters.filter((adapter) => adapter.enabled()).forEach((adapter) => adapter.init());
}

export function trackEvent(eventName: string, params: EventParams = {}): void {
  if (!SHOULD_TRACK) return;
  adapters.filter((adapter) => adapter.enabled()).forEach((adapter) => adapter.track(eventName, params));
}

export function trackPageView(path = window.location.pathname + window.location.search): void {
  const pageTitle = document.title;
  if (GA_ID && SHOULD_TRACK && typeof window.gtag === 'function') {
    window.gtag('config', GA_ID, { page_path: path, page_title: pageTitle });
  }
  trackEvent('page_view', { page_path: path, page_title: pageTitle });
}

export function trackCTA(label: string, destination?: string): void {
  trackEvent('cta_click', { cta_label: label, destination });
}

export function trackPricingCTA(label: string, tier?: string): void {
  trackEvent('pricing_cta_click', { cta_label: label, pricing_tier: tier });
}

export function trackFormSubmit(service: string, budget: string): void {
  trackEvent('contact_form_submission', {
    form_name: 'contact_inquiry',
    service_selected: service,
    budget_range: budget || 'not_selected',
  });
}

export function trackPortfolioClick(projectId: string, projectTitle: string): void {
  trackEvent('portfolio_click', { project_id: projectId, project_title: projectTitle });
}

export function trackOutboundLink(url: string, label?: string): void {
  trackEvent('outbound_link_click', { link_url: url, link_label: label ?? url });
}

export function track404(): void {
  trackEvent('page_not_found', {
    page_path: window.location.pathname,
    page_referrer: document.referrer || 'direct',
  });
}

export function initScrollDepthTracking(): () => void {
  if (!SHOULD_TRACK || typeof window === 'undefined') return () => undefined;
  const milestones = new Set<number>();
  const thresholds = [25, 50, 75, 90, 100];

  const onScroll = () => {
    const total = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
    const percent = Math.min(100, Math.round((window.scrollY / total) * 100));
    thresholds.forEach((threshold) => {
      if (percent >= threshold && !milestones.has(threshold)) {
        milestones.add(threshold);
        trackEvent('scroll_depth', { percent_scrolled: threshold });
      }
    });
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
  return () => window.removeEventListener('scroll', onScroll);
}

export function initTimeOnPageTracking(): () => void {
  if (!SHOULD_TRACK || typeof window === 'undefined') return () => undefined;
  const startedAt = Date.now();
  const timers = [30, 60, 120].map((seconds) => window.setTimeout(() => {
    trackEvent('time_on_page', { seconds });
  }, seconds * 1000));

  const onPageHide = () => {
    trackEvent('time_on_page_exit', { seconds: Math.round((Date.now() - startedAt) / 1000) });
  };

  window.addEventListener('pagehide', onPageHide);
  return () => {
    timers.forEach((timer) => window.clearTimeout(timer));
    window.removeEventListener('pagehide', onPageHide);
  };
}
